import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import { adaptToCreate } from 'data/cfFtpInfo/cfFtpInfo.adapter';
import { CfFtpInfo } from 'data/cfFtpInfo/cfFtpInfo.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newCfFtpInfo } from 'routes/CfFtpInfo/CfFtpInfo.types';
import { cfFtpInfoTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptCfFtpInfoTemplate,
  adaptInitialCfFtpInfoFormData,
} from '../../utils/cfFtpInfoTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/cfFtpInfoTemplateDataToSubmit';
import { Ref } from '../../components/CfFtpInfoServerForm/CfFtpInfoServerForm';
import CfFtpInfoServerForm from '../../components/CfFtpInfoServerForm';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  cfFtpInfoTemplateDef: adaptTemplateVisibilityMode(
    cfFtpInfoTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  cfFtpInfoTemplate: adaptTemplateVisibilityMode(cfFtpInfoTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
});

const mapDispatch = {
  createCfFtpInfo: cfFtpInfoActions.createCfFtpInfo,
  goToCfFtpInfoList:
    cfFtpInfoActions.goToCfFtpInfoList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const CfFtpInfoCreate = ({
  history,
  userId,
  deliveryFunctionId,
  goToCfFtpInfoList,
  createCfFtpInfo,
  userName,
  isAuditEnabled,
  cfFtpInfoTemplateDef,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<CfFtpInfo>(adaptInitialCfFtpInfoFormData(cloneDeep(newCfFtpInfo)));
  const [template, setTemplate] = useState(cfFtpInfoTemplateDef);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as CfFtpInfo);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptCfFtpInfoTemplate(clonedTemplate);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateTLSActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();

    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(
        distributorConfigFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateTLSActionPayload();
    if (payload) {
      createCfFtpInfo(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToCfFtpInfoList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <CfFtpInfoServerForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="CfFtpInfoCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('cfFtpInfo.create.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(CfFtpInfoCreate));
